import { useAuth } from '@/context/AuthContext';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { useIdeas, useNotebooks, useCreateIdea, useDefaultNotebook } from '@/hooks/useApi';
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, Search, Notebook, Lightbulb, Star, Settings } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Dashboard = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('ideas');
  const [newIdeaTitle, setNewIdeaTitle] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Data fetching
  const { data: ideas, isLoading: ideasLoading } = useIdeas();
  const { data: notebooks, isLoading: notebooksLoading } = useNotebooks();
  const { data: defaultNotebook } = useDefaultNotebook();
  const { mutate: createIdea } = useCreateIdea();

  const handleCreateIdea = () => {
    if (!newIdeaTitle.trim() || !defaultNotebook) return;

    createIdea({
      title: newIdeaTitle,
      content: '',
      notebook_id: defaultNotebook.id,
      status: 'draft',
      is_favorite: false,
    });

    setNewIdeaTitle('');
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    // Implement search functionality
    console.log('Searching for:', searchTerm);
  };

  if (ideasLoading || notebooksLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-hero">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-primary mb-4 mx-auto"></div>
          <p className="text-primary animate-pulse">Loading your ideas...</p>
        </div>
      </div>
    );
  }

  return (
    <ProtectedRoute>
      <div className="min-h-screen bg-gradient-hero">
        <div className="container mx-auto px-4 py-8">
          {/* Header */}
          <header className="flex justify-between items-center mb-8">
            <div className="flex items-center space-x-4">
              <h1 className="text-2xl font-bold text-foreground">Founder Labs Ideas</h1>
              <span className="bg-primary/10 text-primary px-3 py-1 rounded-full text-sm">
                {user?.email}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <Button variant="outline" size="sm" onClick={() => navigate('/settings')}>
                <Settings className="h-4 w-4 mr-2" />
                Settings
              </Button>
              <Button variant="outline" size="sm" onClick={() => signOut()}>
                Sign Out
              </Button>
            </div>
          </header>

          {/* Main Content */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Sidebar */}
            <aside className="lg:col-span-1 space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Notebook className="h-5 w-5 mr-2 text-primary" />
                    Notebooks
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {notebooks?.map((notebook) => (
                    <Button
                      key={notebook.id}
                      variant="ghost"
                      className="w-full justify-start"
                      onClick={() => setActiveTab(`notebook-${notebook.id}`)}
                    >
                      <div className="flex items-center space-x-2">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: notebook.color }}
                        ></div>
                        <span>{notebook.name}</span>
                      </div>
                    </Button>
                  ))}
                  <Button
                    variant="ghost"
                    className="w-full justify-start text-primary"
                    onClick={() => navigate('/notebooks/new')}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    New Notebook
                  </Button>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Lightbulb className="h-5 w-5 mr-2 text-primary" />
                    Quick Actions
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <Button
                    variant="ghost"
                    className="w-full justify-start"
                    onClick={() => setActiveTab('new-idea')}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    New Idea
                  </Button>
                  <Button
                    variant="ghost"
                    className="w-full justify-start"
                    onClick={() => navigate('/ideas/favorites')}
                  >
                    <Star className="h-4 w-4 mr-2" />
                    Favorites
                  </Button>
                </CardContent>
              </Card>
            </aside>

            {/* Main Content Area */}
            <main className="lg:col-span-3">
              {/* Search and New Idea */}
              <div className="flex flex-col sm:flex-row gap-4 mb-6">
                <form onSubmit={handleSearch} className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      type="search"
                      placeholder="Search your ideas..."
                      className="pl-10"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </form>
                <Button className="sm:w-auto w-full" onClick={() => setActiveTab('new-idea')}>
                  <Plus className="h-4 w-4 mr-2" />
                  New Idea
                </Button>
              </div>

              {/* New Idea Form */}
              {activeTab === 'new-idea' && (
                <Card className="mb-6">
                  <CardContent className="pt-6">
                    <div className="flex gap-2">
                      <Input
                        placeholder="Idea title..."
                        value={newIdeaTitle}
                        onChange={(e) => setNewIdeaTitle(e.target.value)}
                        className="flex-1"
                      />
                      <Button
                        onClick={handleCreateIdea}
                        disabled={!newIdeaTitle.trim()}
                      >
                        Add Idea
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Ideas List */}
              <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
                <TabsList className="grid grid-cols-2 md:grid-cols-4">
                  <TabsTrigger value="ideas">All Ideas</TabsTrigger>
                  <TabsTrigger value="drafts">Drafts</TabsTrigger>
                  <TabsTrigger value="published">Published</TabsTrigger>
                  <TabsTrigger value="archived">Archived</TabsTrigger>
                </TabsList>

                <TabsContent value="ideas">
                  <div className="space-y-4">
                    {ideas?.length === 0 ? (
                      <Card>
                        <CardContent className="text-center py-12">
                          <Lightbulb className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                          <p className="text-muted-foreground">No ideas yet. Start by creating your first idea!</p>
                        </CardContent>
                      </Card>
                    ) : (
                      ideas?.map((idea) => (
                        <Card key={idea.id} className="hover:shadow-md transition-shadow">
                          <CardContent className="pt-6">
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <h3 className="font-semibold text-foreground mb-1">{idea.title}</h3>
                                <p className="text-sm text-muted-foreground line-clamp-2">
                                  {idea.content || 'No description'}
                                </p>
                              </div>
                              <div className="flex items-center space-x-2 ml-4">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => navigate(`/ideas/${idea.id}/edit`)}
                                >
                                  Edit
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className={idea.is_favorite ? 'text-yellow-500' : ''}
                                  onClick={() => {
                                    // Toggle favorite
                                    console.log('Toggle favorite for idea:', idea.id);
                                  }}
                                >
                                  <Star className="h-4 w-4" fill={idea.is_favorite ? 'currentColor' : 'none'} />
                                </Button>
                              </div>
                            </div>
                            <div className="mt-3 flex items-center justify-between text-sm">
                              <span className="text-muted-foreground">
                                {new Date(idea.updated_at).toLocaleDateString()}
                              </span>
                              <span className={`px-2 py-1 rounded-full text-xs ${
                                idea.status === 'draft' ? 'bg-gray-100 text-gray-600' :
                                idea.status === 'published' ? 'bg-blue-100 text-blue-600' :
                                'bg-gray-200 text-gray-500'
                              }`}>
                                {idea.status}
                              </span>
                            </div>
                          </CardContent>
                        </Card>
                      ))
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="drafts">
                  <div className="space-y-4">
                    {ideas?.filter(idea => idea.status === 'draft').length === 0 ? (
                      <Card>
                        <CardContent className="text-center py-12">
                          <p className="text-muted-foreground">No draft ideas found.</p>
                        </CardContent>
                      </Card>
                    ) : (
                      ideas?.filter(idea => idea.status === 'draft').map((idea) => (
                        <Card key={idea.id}>
                          <CardContent className="pt-6">
                            <h3 className="font-semibold">{idea.title}</h3>
                            <p className="text-sm text-muted-foreground mt-1">
                              {idea.content || 'No description'}
                            </p>
                          </CardContent>
                        </Card>
                      ))
                    )}
                  </div>
                </TabsContent>
              </Tabs>
            </main>
          </div>
        </div>
      </div>
    </ProtectedRoute>
  );
};

export default Dashboard;